import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { listen, platform } from "./tauri-web";
import { saveFileDialog } from "./tauri-web";
import { writeTextFile } from "./tauri-web";
import { supabase } from "./supabase";
import { AiConsent, AI_CONSENT_KEY, signInIos, signInMac } from "./ios-native-kit";
import { AccountMenu } from "./AccountMenu";
import { AddFundsMenu } from "./AddFundsMenu";
import {
  store, relativeDays, themeLabel,
  SENIORITIES, SENIORITY_LABELS, INTERVIEW_TYPES, TYPE_LABELS, ROLE_SUGGESTIONS,
  PRACTICE_MODES, MODE_LABELS, MODE_HINTS, SIMULATE_SECONDS, THEME_NEXT_STEPS,
  PROFILE_RESUME_KEY, parseReport,
  type Session, type SessionQuestion, type Seniority, type InterviewType,
  type PracticeMode, type BestAnswer, type Feedback, type ThemeCount, type SessionReport, parseFeedback,
} from "./store";
import { generateQuestions, getFeedback, getSessionReport, structureBrief, openWebLogin, BrainError, type BrainErrorCode, type BriefStructure } from "./brain";
import { OrioraBadge } from "./OrioraBadge";
import { buildExport } from "./export";
import { BackupMenu } from "./BackupMenu";
import { MicButton, SpeakButton } from "./chat/voice";
import { PhotoButton } from "./chat/photo";
import Chat from "./chat/Chat";
import CallOverlay from "./live/CallOverlay";
import type { InterviewCallContext } from "./live/useLiveSession";
import glyphSession from "./assets/glyph-session.svg";
import glyphCoaching from "./assets/glyph-coaching.svg";
import markBestAnswer from "./assets/mark-best-answer.svg";
import emptyNoPractice from "./assets/empty-no-practice.svg";
import {
  Sparkles, Trash2, X, Lock, WifiOff, RefreshCw, Clock, AlertCircle,
  History, Copy, Check, Coins, Briefcase, MessageSquare, BookOpen, ChevronDown, Star, ShieldCheck,
} from "lucide-react";

const LIST_CAP = 50000;

// SCREENSHOT=0 — sim-only auth/consent bypass for identity screenshots. MUST stay 0 in committed/shipped code.
const SCREENSHOT = 0;

/**
 * spoken practice answers — the control is the question being put to you.
 * Replaces the lucide `Sparkles` that 20 of the fleet's 26 chat launchers rendered in common;
 * the button outline changes with it, because one glyph in two colours is still one control
 * (Guideline 4.3(a)).
 */
function AskMark({ size = 19 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M9.2 9a2.9 2.9 0 1 1 3.9 2.7c-.7.3-1.1 1-1.1 1.7v.6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      <circle cx="12" cy="17.4" r="1.15" fill="currentColor" />
    </svg>
  );
}

export default function App() {
  const [mounted, setMounted] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [email, setEmail] = useState<string | null>(null);
  // AI-consent gate (APP-READY-STANDARD §2 #62, Apple Guideline 5.1.1(i)). Shown ONCE on first
  // launch before anything else — names the data sent + the AI vendors. Persisted in localStorage.
  const [consented, setConsented] = useState<boolean>(() => {
    try { return localStorage.getItem(AI_CONSENT_KEY) === "1"; } catch { return false; }
  });

  // A deleted/revoked account keeps a locally-valid (unexpired) JWT, so getSession() still
  // returns it and the app looks signed-in. getUser() asks the SERVER — on a real auth
  // rejection (401/403) we purge the dead session and drop to login. Network errors (no
  // status) are ignored so an offline-but-valid user is never signed out.
  const revalidate = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const { error } = await supabase.auth.getUser();
      const status = (error as { status?: number } | null)?.status;
      if (error && (status === 401 || status === 403)) {
        try { await supabase.auth.signOut({ scope: "local" }); } catch { /* ignore */ }
        setEmail(null); setAuthed(false);
      }
    } catch { /* network/unknown → keep session (offline-tolerant) */ }
  }, []);

  useEffect(() => {
    // onAuthStateChange fires INITIAL_SESSION after async filesystem storage loads —
    // avoids the cold-launch race where getSession() returned null before storage was ready.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, session) => {
      setAuthed(session !== null);
      setEmail(session?.user?.email ?? null);
      setMounted(true);
      if (_e === 'INITIAL_SESSION' && session) void revalidate();
    });
    const timeout = setTimeout(() => setMounted(true), 3000);
    return () => { clearTimeout(timeout); subscription.unsubscribe(); };
  }, [revalidate]);

  // Re-validate whenever the app returns to the foreground (catches "deleted my account,
  // came back, still signed in").
  useEffect(() => {
    const recheck = () => { if (document.visibilityState === "visible") void revalidate(); };
    document.addEventListener("visibilitychange", recheck);
    window.addEventListener("focus", recheck);
    return () => { document.removeEventListener("visibilitychange", recheck); window.removeEventListener("focus", recheck); };
  }, [revalidate]);

  // Deep-link auth: web login returns interview-prep-tutor://auth#access_token=…&refresh_token=…
  useEffect(() => {
    let unlisten: (() => void) | null = null;
    listen<string>("auth:deep-link", async (e) => {
      try {
        const u = new URL(e.payload);
        const frag = new URLSearchParams(u.hash.replace(/^#/, ""));
        const access_token = frag.get("access_token");
        const refresh_token = frag.get("refresh_token");
        if (access_token && refresh_token) await supabase.auth.setSession({ access_token, refresh_token });
      } catch (err) {
        console.error("deep-link auth failed", err);
      }
    }).then((u) => { unlisten = u; });
    return () => { if (unlisten) unlisten(); };
  }, []);

  // Account-deletion handback: the in-app delete window finished + navigated to
  // interview-prep-tutor://deleted. Sign the app out so it drops to the logged-out screen.
  useEffect(() => {
    let unlisten: (() => void) | null = null;
    listen("account:deleted", async () => {
      try { await supabase.auth.signOut({ scope: "local" }); } catch { /* ignore */ }
      setEmail(null); setAuthed(false);
    }).then((u) => { unlisten = u; });
    return () => { if (unlisten) unlisten(); };
  }, []);

  // First-launch AI-consent gate — before login + before any AI feature is reachable.
  // The notice text is THE LOCKED fleet copy inside AiConsent (only the app name varies).
  if (!consented && !SCREENSHOT) {
    return <AiConsent brand="Oriora Interview"
      onAccept={() => { try { localStorage.setItem(AI_CONSENT_KEY, "1"); } catch { /* still consented this session */ } setConsented(true); }} />;
  }
  if (!mounted && !SCREENSHOT) return <LoadingGate />;
  if (!authed && !SCREENSHOT) return <Login />;
  return <Home email={SCREENSHOT ? "you@oriora.app" : email} onSignedOut={() => { setEmail(null); setAuthed(false); }} />;
}

function LoadingGate() {
  return (
    <main style={{ height: "100dvh", display: "flex", flexDirection: "column", overflowY: "auto", WebkitOverflowScrolling: "touch" as any, boxSizing: "border-box", padding: "1.5rem" }}>
      <div style={{ textAlign: "center" }}>
        <div className="empty-art" style={{ marginBottom: "0.9rem" }}><Briefcase size={34} /></div>
        <div className="muted row" style={{ justifyContent: "center", gap: "0.4rem", fontSize: "var(--fs-sm)" }}>
          <Sparkles size={13} className="pulse-soft" style={{ color: "var(--accent)" }} /> Opening your practice room…
        </div>
      </div>
    </main>
  );
}

function Login() {
  const [busy, setBusy] = useState(false);
  return (
    <main style={{ height: "100dvh", display: "flex", flexDirection: "column", overflowY: "auto", WebkitOverflowScrolling: "touch" as any, boxSizing: "border-box", padding: "max(1.5rem, env(safe-area-inset-top)) 1.5rem max(1.5rem, env(safe-area-inset-bottom))" }}>
      <div className="card fade-up empty-glow" style={{ width: "100%", maxWidth: 380, padding: "2.25rem 2rem", textAlign: "center", borderRadius: "var(--radius-lg)" }}>
        <div className="empty-art" style={{ marginBottom: "0.9rem" }}><Briefcase size={34} /></div>
        <div className="eyebrow" style={{ justifyContent: "center", marginBottom: "0.35rem" }}>Oriora Interview</div>
        <h1 className="serif" style={{ fontSize: "1.6rem", margin: "0 0 0.5rem" }}>Sign in with Oriora</h1>
        <p className="muted" style={{ fontSize: "var(--fs-xs)", lineHeight: 1.6, marginBottom: "1.4rem" }}>
          Uses your Oriora account — opens to sign in, then returns here. Your practice history and
          saved answers live on this device; AI features send only what each request needs, and Oriora
          doesn't retain it. You can also back up to your own GitHub or Google Drive.
        </p>
        <button className="btn btn-primary btn-pill" style={{ width: "100%" }} disabled={busy}
          onClick={async () => {
            setBusy(true);
            // iOS → in-app ASWebAuthenticationSession (Apple Guideline 4, #61); desktop → in-app login window.
            try {
              const p = platform();
              if (p === "ios") await signInIos("interview-prep-tutor");
              else if (p === "macos") await signInMac("interview-prep-tutor");
              // Web: same-origin login. Go to the site login and return to this app's path; the
              // Supabase session is shared on the orioralabs.com origin, so we come back signed in.
              else if (p === "web") window.location.href = "/login?next=/interview-prep-tutor/";
              else await openWebLogin();
            }
            catch { /* user dismissed the sheet or it failed — reset and let them retry */ }
            finally { setBusy(false); }
          }}>
          {busy ? (<><Sparkles size={14} className="pulse-soft" /> Signing in…</>) : "Sign in with Oriora"}
        </button>
        <div className="muted row" style={{ justifyContent: "center", gap: "0.3rem", fontSize: "var(--fs-xxs)", marginTop: "1rem" }}>
          <Lock size={11} /> Your history is stored here on your device unless you back it up — Oriora doesn't retain it
        </div>
      </div>
    </main>
  );
}

function Home({ email, onSignedOut }: { email: string | null; onSignedOut: () => void }) {
  const [sessions, setSessions] = useState<Session[]>([]);
  const [themes, setThemes] = useState<ThemeCount[]>([]);
  const [best, setBest] = useState<BestAnswer[]>([]);
  const [practiceId, setPracticeId] = useState<string | null>(null);
  const [reviewFor, setReviewFor] = useState<Session | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Session | null>(null);
  // Chat panel (ADDITIVE): a natural-language front door over the SAME local capabilities.
  const [chatOpen, setChatOpen] = useState(false);
  const [offline, setOffline] = useState(typeof navigator !== "undefined" && navigator.onLine === false);
  // CSV export uses the desktop save-file dialog, which doesn't exist on iOS — hide the
  // affordance there. Default to showing it (plain web preview), then correct after mount.
  const [isDesktop, setIsDesktop] = useState(true);
  useEffect(() => { try { setIsDesktop(platform() !== "ios" && platform() !== "android"); } catch { /* keep default */ } }, []);

  useEffect(() => {
    const goOnline = () => setOffline(false);
    const goOffline = () => setOffline(true);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => { window.removeEventListener("online", goOnline); window.removeEventListener("offline", goOffline); };
  }, []);

  const setupRef = useRef<HTMLDivElement>(null);
  const exportRef = useRef<(() => void) | null>(null);

  // R1 — THE CHAIR: the most recent in-progress session's next question leads the home.
  const [lead, setLead] = useState<{ s: Session; q: SessionQuestion } | null>(null);
  // The setup card leads only when nothing is in progress; otherwise it folds behind
  // "+ Start another practice" so the chair keeps the room.
  const [showSetup, setShowSetup] = useState(false);

  const reload = useCallback(async () => {
    const ss = await store.listSessions({ limit: LIST_CAP });
    setSessions(ss);
    setThemes(await store.themeCounts());
    setBest(await store.listBestAnswers());
    const inProg = ss.find((s) => s.question_count > 0 && s.answered_count < s.question_count);
    if (inProg) {
      const qs = await store.listQuestions(inProg.id);
      const nq = qs.find((q) => !q.answered_at);
      setLead(nq ? { s: inProg, q: nq } : null);
    } else {
      setLead(null);
    }
  }, []);
  useEffect(() => { void reload(); }, [reload]);

  // Native-menu actions (Actions menu in the macOS menu bar): New practice / Export.
  useEffect(() => {
    let unlisten: (() => void) | null = null;
    listen<string>("menu:action", (e) => {
      switch (e.payload) {
        case "practice":
          setShowSetup(true);
          setTimeout(() => {
            setupRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
            setupRef.current?.querySelector<HTMLInputElement>("input")?.focus();
          }, 60);
          break;
        case "export": exportRef.current?.(); break;
      }
    }).then((u) => { unlisten = u; });
    return () => { if (unlisten) unlisten(); };
  }, []);

  const answeredTotal = useMemo(() => sessions.reduce((s, x) => s + x.answered_count, 0), [sessions]);

  const doExport = useCallback(() => {
    if (sessions.length === 0) return;
    void (async () => {
      const questionsBySession = new Map<string, SessionQuestion[]>();
      for (const s of sessions) questionsBySession.set(s.id, await store.listQuestions(s.id));
      const { csv, filename } = buildExport(sessions, questionsBySession);
      const p = await saveFileDialog({ defaultPath: filename });
      if (p) await writeTextFile(p, csv);
    })();
  }, [sessions]);
  exportRef.current = doExport;

  async function doDelete(id: string) {
    setConfirmDelete(null);
    await store.deleteSession(id);
    await reload();
  }

  return (
    <main style={{
      maxWidth: 720, margin: "0 auto",
      // Safe-area aware so content clears the iPhone notch/home-indicator (viewport-fit=cover).
      padding: "max(1.5rem, env(safe-area-inset-top)) max(1.25rem, env(safe-area-inset-right)) calc(2rem + env(safe-area-inset-bottom)) max(1.25rem, env(safe-area-inset-left))",
    }}>
      <header className="row" style={{ justifyContent: "space-between", marginBottom: "1.1rem", alignItems: "flex-start" }}>
        <div className="row" style={{ gap: "0.55rem" }}>
          <span className="thumb" style={{ width: 38, height: 38, borderRadius: 11 }}><img src={glyphSession} width={20} height={20} alt="" /></span>
          <h1 className="serif" style={{ fontSize: "1.5rem", margin: 0 }}>Interview</h1>
        </div>
        <div className="row" style={{ gap: "0.5rem", alignItems: "center" }}>
          <BackupMenu
            appName="Interview" fileSlug="interview" appScheme="interview-prep-tutor"
            hasData={sessions.length > 0} onRestored={reload}
            onExportCsv={isDesktop && sessions.length > 0 ? doExport : undefined}
          />
          <AccountMenu email={email} appSlug="interview-prep-tutor" appName="Oriora Interview" onSignedOut={onSignedOut} />
          <AddFundsMenu />
        </div>
      </header>

      {offline && (
        <div role="status" className="notice notice-warn fade-up" style={{ marginBottom: "1rem" }}>
          <WifiOff size={14} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>Practice needs a connection for questions and feedback — your saved history still opens fine.</span>
        </div>
      )}

      {/* R4 — the stat dashboard is one worded line now (hero + duplicate stat card retired). */}
      {sessions.length > 0 && (
        <div className="practice-summary fade-up" style={{ marginBottom: "1.1rem" }}>
          <img src={glyphCoaching} width={15} height={15} alt="" style={{ flexShrink: 0 }} />
          <span>
            <strong>{spellCount(answeredTotal, "answer", true)}</strong> across {spellCount(sessions.length, "session")}
            {themes.length > 0 ? <> — {themes.slice(0, 2).map((t) => themeLabel(t.theme).toLowerCase()).join(" and ")} keep{themes.slice(0, 2).length === 1 ? "s" : ""} coming up.</> : "."}
          </span>
        </div>
      )}

      {/* R1 — the chair: the in-progress session's next question leads. */}
      {lead && (
        <section className="lead fade-up" style={{ marginBottom: "1rem" }}>
          <div className="lead-meta">
            {lead.s.role} · {SENIORITY_LABELS[lead.s.seniority]} · {TYPE_LABELS[lead.s.interview_type]} · {MODE_LABELS[lead.s.mode]}{lead.s.company ? ` · ${lead.s.company}` : ""}
          </div>
          <h2 className="lead-question">{lead.q.question}</h2>
          <div className="lead-actions">
            <button className="btn btn-primary btn-pill" onClick={() => setPracticeId(lead.s.id)}>Continue</button>
            <button className="btn-ghost" onClick={() => setReviewFor(lead.s)}>Review session</button>
          </div>
          <div className="lead-progress"><span style={{ width: `${Math.round((lead.s.answered_count / Math.max(1, lead.s.question_count)) * 100)}%` }} /></div>
          <div className="tnum" style={{ fontSize: "var(--fs-xxs)", color: "var(--ink-3)", marginTop: 6 }}>
            Question {Math.min(lead.s.answered_count + 1, lead.s.question_count)} of {lead.s.question_count} · {lead.s.answered_count} answered
          </div>
        </section>
      )}

      {/* Setup: leads the home when nothing is in progress; folds behind a quiet dashed
          affordance when the chair has the room. */}
      {lead && !showSetup ? (
        <button
          onClick={() => setShowSetup(true)}
          style={{
            width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: "0.45rem",
            background: "none", border: "1px dashed var(--border)", color: "var(--ink-2)",
            borderRadius: "var(--radius)", padding: "0.85rem", fontSize: "var(--fs-sm)", cursor: "pointer",
          }}>
          + Start another practice
        </button>
      ) : (
        <div ref={setupRef}>
          <StartPractice offline={offline} onStarted={(id) => { setShowSetup(false); setPracticeId(id); void reload(); }} />
        </div>
      )}

      {best.length > 0 && <BestAnswersSection best={best} onChanged={reload} />}

      <div className="sec-head">
        <h2><History size={17} style={{ color: "var(--accent)" }} /> Past sessions {sessions.length > 0 && <span className="muted tnum" style={{ fontWeight: 400 }}>({sessions.length})</span>}</h2>
      </div>

      {sessions.length === 0 ? (
        <div className="card fade-up empty-glow" style={{ padding: "2.75rem 1.75rem", textAlign: "center", borderRadius: "var(--radius-lg)" }}>
          <div className="empty-art" style={{ background: "var(--accent-soft)" }}><img src={emptyNoPractice} width={54} height={45} alt="" /></div>
          <div className="serif" style={{ fontSize: "1.25rem", margin: "0.85rem 0 0.4rem" }}>No practice yet</div>
          <div className="muted" style={{ fontSize: "var(--fs-sm)", lineHeight: 1.55, maxWidth: 320, margin: "0 auto" }}>
            Pick your role above (paste the posting and your resume for real tailoring) — five
            questions, coaching on every answer, a session debrief, and a history that shows
            what keeps coming up.
          </div>
        </div>
      ) : (
        <div className="shelf">
          {sessions.map((s) => (
            <SessionTile
              key={s.id} s={s}
              onOpen={() => (s.answered_count < s.question_count ? setPracticeId(s.id) : setReviewFor(s))}
              onReview={() => setReviewFor(s)}
              onDelete={() => setConfirmDelete(s)}
            />
          ))}
        </div>
      )}

      {practiceId && (
        <PracticeModal
          sessionId={practiceId}
          offline={offline}
          onClose={() => { setPracticeId(null); void reload(); }}
          onReviewNow={(s) => { setPracticeId(null); setReviewFor(s); void reload(); }}
        />
      )}

      {reviewFor && (
        <ReviewModal session={reviewFor} offline={offline} onChanged={reload}
          onClose={() => { setReviewFor(null); void reload(); /* pick up ★ saves made inside the review */ }} />
      )}

      {confirmDelete && (
        <ModalShell title="Delete session?" onCancel={() => setConfirmDelete(null)}>
          <p className="muted" style={{ fontSize: "var(--fs-sm)", margin: "0 0 1.25rem", lineHeight: 1.55 }}>
            Delete the <strong style={{ color: "var(--ink-1)" }}>{confirmDelete.role}</strong> practice from {relativeDays(confirmDelete.created_at)},
            including its answers and feedback? This can't be undone.
          </p>
          <div className="row" style={{ justifyContent: "flex-end", gap: "0.5rem" }}>
            <button className="btn btn-pill" onClick={() => setConfirmDelete(null)}>Cancel</button>
            <button className="btn btn-danger btn-pill" onClick={() => doDelete(confirmDelete.id)}>Delete</button>
          </div>
        </ModalShell>
      )}

      {/* Chat panel — the whole app by typing: start a practice, coach an answer, keep best
          answers, search, back up. Purely additive to the screens. */}
      {!chatOpen && (
        <button className="rbc-launch" onClick={() => setChatOpen(true)} aria-label="Open chat assistant">
          <AskMark size={19} />
        </button>
      )}
      <aside className={`rbc-sheet ${chatOpen ? "open" : ""}`} aria-hidden={!chatOpen}>
        <div className="rbc-head">
          <span className="row" style={{ gap: "0.35rem" }}><Sparkles size={14} /> Coach — start, practice, refine, and organize by typing</span>
          <button className="link" onClick={() => setChatOpen(false)}>Close ↓</button>
        </div>
        <Chat onChanged={reload} />
      </aside>
    </main>
  );
}

// PracticeHero RETIRED (R4) — the stat dashboard is now one worded .practice-summary line on Home.

// ── Start a practice — AI call 1 (ONE billed call → 5 questions with hints) ──────
type AskErr = { code: BrainErrorCode | "empty"; message: string };

// m:ss for the answer clock (an on-device counter, stored with the answer; not part of any billed call).
function fmtClock(s: number): string {
  return `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, "0")}`;
}

// R4 — numbers survive as words in the one-line summary ("Eight answers across three sessions").
const NUM_WORDS = ["no", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten", "eleven", "twelve"];
function spellCount(n: number, noun: string, cap = false): string {
  let w = n >= 0 && n < NUM_WORDS.length ? NUM_WORDS[n] : String(n);
  if (cap) w = w.charAt(0).toUpperCase() + w.slice(1);
  return `${w} ${noun}${n === 1 ? "" : "s"}`;
}

// First-open example content (APP-READY: empty states say what the feature is for; NO fake
// data rows — the example is labeled, lives only in the input boxes, and still runs the real,
// disclosed flow when the user taps Prepare).
const EXAMPLE_BRIEF =
  "Example — Junior Frontend Engineer at Fernwood Health (remote, EU). We build patient-facing " +
  "web apps in React + TypeScript. You'll own small features end-to-end, work closely with " +
  "designers, and care about accessibility. Nice to have: testing experience, GraphQL.";
const EXAMPLE_RESUME =
  "Example — Sam Rivera, frontend developer, 2 years. Built an appointment-booking UI in React " +
  "at a health startup; cut form drop-off 18%. Migrated legacy jQuery views to TypeScript. " +
  "Comfortable with Jest, CSS, and accessibility audits. B.Sc. Computer Science.";

function StartPractice({ offline, onStarted }: { offline: boolean; onStarted: (sessionId: string) => void }) {
  const [role, setRole] = useState("");
  const [seniority, setSeniority] = useState<Seniority>("mid");
  const [itype, setItype] = useState<InterviewType>("mixed");
  const [mode, setMode] = useState<PracticeMode>("prep");
  const [showTuning, setShowTuning] = useState(false);
  // The OPEN input (Bogdan's review: the rigid form "made no sense" — no company, no country,
  // no useful info). Paste anything — the job posting, company notes — or photograph it (📷,
  // read on-device); one Oriora call turns it into a structured understanding shown back.
  const [brief, setBrief] = useState("");
  // The resume (parity-gate mock loop): kept on-device in the profile store, prefilled here,
  // sent ONLY with the questions call so the set targets the user's own stated experience.
  const [resume, setResume] = useState("");
  const [showResume, setShowResume] = useState(false);
  useEffect(() => {
    void (async () => {
      try {
        const saved = await store.getProfile(PROFILE_RESUME_KEY);
        if (saved) { setResume(saved); setShowResume(true); }
      } catch { /* profile store unavailable — the field just starts empty */ }
    })();
  }, []);
  const [understood, setUnderstood] = useState<BriefStructure | null>(null);
  const [busy, setBusy] = useState<false | "reading" | "generating">(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [err, setErr] = useState<AskErr | null>(null);

  const needsReading = brief.trim().length > 0 && understood === null;

  async function readBriefing() {
    setBusy("reading"); setErr(null);
    try {
      const u = await structureBrief(brief);
      setUnderstood(u);
      setRole(u.role);
      if (u.seniority) setSeniority(u.seniority);
      if (u.interview_type) setItype(u.interview_type);
    } catch (e) {
      if (e instanceof BrainError) setErr({ code: e.code, message: e.message });
      else setErr({ code: "unavailable", message: "Couldn't read the briefing — please try again, or fill the role in yourself." });
    } finally { setBusy(false); }
  }

  // The ONE primary action: prepare the practice questions. If a briefing was pasted but not yet
  // structured, read it first (one call) THEN generate — so the single "Prepare my questions" CTA
  // always does the whole thing and the user never has to hunt for a second button. (Bogdan's
  // review 2026-07-11: the old morphing button hid the generate step behind "Read my briefing".)
  async function start() {
    let understanding = understood;
    if (brief.trim() && understanding === null) {
      setBusy("reading"); setErr(null);
      try {
        understanding = await structureBrief(brief);
        setUnderstood(understanding);
        setRole(understanding.role);
        if (understanding.seniority) setSeniority(understanding.seniority);
        if (understanding.interview_type) setItype(understanding.interview_type);
      } catch (e) {
        if (e instanceof BrainError) setErr({ code: e.code, message: e.message });
        else setErr({ code: "unavailable", message: "Couldn't read the briefing — please try again, or fill the role in yourself." });
        setBusy(false); return;
      }
    }
    // Use the FRESH understanding (state setters above aren't applied synchronously).
    const r = (understanding?.role || role).trim();
    if (!r) { setErr({ code: "empty", message: "What role are you practicing for? Type it, or paste a briefing above." }); setBusy(false); return; }
    const sen = understanding?.seniority ?? seniority;
    const it = understanding?.interview_type ?? itype;
    setBusy("generating"); setErr(null);
    try {
      // Keep the resume for next time (on-device profile store; best-effort).
      try { await store.setProfile(PROFILE_RESUME_KEY, resume.trim()); } catch { /* non-fatal */ }
      const questions = await generateQuestions(r, sen, it, brief, resume);
      const session = await store.createSession({
        role: r, seniority: sen, interview_type: it, mode,
        company: understanding?.company ?? "", focus: understanding?.focus ?? "", brief: brief.trim(),
        questions,
      });
      onStarted(session.id);
    } catch (e) {
      if (e instanceof BrainError) setErr({ code: e.code, message: e.message });
      else setErr({ code: "unavailable", message: "Couldn't start the session — please try again." });
    } finally { setBusy(false); }
  }

  // The primary CTA is ALWAYS "prepare my questions" (it auto-reads a briefing first when needed).
  const primary = start;

  return (
    <div className="recap fade-up" style={{ padding: "1.1rem 1.25rem", marginBottom: "1.1rem" }}>
      <div className="eyebrow" style={{ flexWrap: "wrap" }}>
        <Sparkles size={14} style={{ flexShrink: 0 }} /> Start a practice
      </div>
      <div className="muted" style={{ fontSize: "var(--fs-sm)", marginTop: 8, lineHeight: 1.55 }}>
        Questions tailored to <strong style={{ color: "var(--ink-2)" }}>your</strong> interview — paste the
        job posting or anything useful below (or photograph it) and the AI structures it, or just name the role.
      </div>

      <label style={{ ...fieldLabel, marginTop: "0.9rem" }}>
        About this interview <span className="muted" style={{ fontWeight: 400 }}>(optional — job posting, company, country, your interests…)</span>
      </label>
      <div className="row" style={{ gap: "0.4rem", alignItems: "flex-end", flexWrap: "nowrap" }}>
        <textarea rows={3} placeholder="Paste anything useful — the posting text, the firm, the team, what you want to focus on…"
          value={brief}
          onChange={(e) => { setBrief(e.target.value); setUnderstood(null); }}
          style={{ flex: 1, minWidth: 0, resize: "vertical" }} />
        <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 6, flexShrink: 0 }}>
          {/* Photograph the posting — read by on-device OCR; only the TEXT lands in the box. */}
          <PhotoButton
            onNotice={(m) => setNotice(m)}
            onText={(t) => { setBrief((v) => (v ? v + "\n\n" + t : t)); setUnderstood(null); setNotice(null); }}
            disabled={busy !== false} />
          <MicButton onText={(t) => { setBrief((v) => (v ? v + " " : "") + t); setUnderstood(null); }} disabled={busy !== false} />
        </div>
      </div>
      {notice && <div className="muted fade-up" style={{ fontSize: "var(--fs-xs)", marginTop: 6 }}>{notice}</div>}

      {/* First-open example — labeled content into the two boxes, nothing fake stored. */}
      {!role.trim() && !brief.trim() && !resume.trim() && (
        <button type="button" className="link-quiet" style={{ marginTop: 6, fontSize: "var(--fs-xs)", background: "none", border: "none", padding: 0, cursor: "pointer" }}
          onClick={() => { setBrief(EXAMPLE_BRIEF); setResume(EXAMPLE_RESUME); setShowResume(true); setUnderstood(null); }}>
          Just looking? Try an example posting + resume →
        </button>
      )}

      {/* Resume — the mock loop's second tailoring input. On-device; sent only with the
          questions call. Collapsed behind one line unless a saved resume already exists. */}
      <button type="button" className="link-quiet row" style={{ gap: "0.3rem", marginTop: "0.9rem", textDecoration: "none" }}
        onClick={() => setShowResume((v) => !v)} aria-expanded={showResume}>
        <ChevronDown size={12} style={{ transform: showResume ? "none" : "rotate(-90deg)", transition: "transform 120ms ease" }} />
        {resume.trim() ? "Your resume (saved on this device)" : "Add your resume (optional)"}
      </button>
      {showResume && (
        <div className="fade-up">
          <div className="row" style={{ gap: "0.4rem", alignItems: "flex-end", flexWrap: "nowrap", marginTop: "0.5rem" }}>
            <textarea rows={4} placeholder="Paste your resume (or the parts that matter) — questions get aimed at your own experience…"
              value={resume} onChange={(e) => setResume(e.target.value)}
              // Persist on blur too (not just on start) so "clear the box to forget it" is true.
              onBlur={(e) => { void store.setProfile(PROFILE_RESUME_KEY, e.target.value.trim()).catch(() => { /* non-fatal */ }); }}
              style={{ flex: 1, minWidth: 0, resize: "vertical" }} />
            <span style={{ flexShrink: 0 }}><MicButton onText={(t) => setResume((v) => (v ? v + " " : "") + t)} disabled={busy !== false} /></span>
          </div>
          <div className="muted" style={{ fontSize: "var(--fs-xxs)", marginTop: 4, lineHeight: 1.5 }}>
            {/* §7b: qualified the DEVICE claim, not just the AI path. The resume lives in the SQLite
                `profile` table (src-tauri/src/db.rs:203) and backup.rs::export dumps EVERY user table,
                so it rides Cloud Backup — "kept on this device" alone was an unqualified absolute.
                Whole claim on ONE line so the per-line gate matches. */}
            Kept on this device unless you back it up, and sent only with the questions call to tailor them — Oriora doesn't retain it. Clear the box to forget it.
          </div>
        </div>
      )}

      {understood && (
        <div className="notice notice-info fade-up" style={{ marginTop: "0.75rem" }}>
          <Check size={15} style={{ color: "var(--accent)", flexShrink: 0, marginTop: 2 }} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: "var(--fs-xs)", fontWeight: 600, marginBottom: 2 }}>Here's what I understood — adjust anything below, then start</div>
            {understood.summary && <div className="muted" style={{ fontSize: "var(--fs-xs)", lineHeight: 1.55 }}>{understood.summary}</div>}
            {(understood.company || understood.focus) && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: "0.3rem", marginTop: 6 }}>
                {understood.company && <span className="chip chip-mini">{understood.company}</span>}
                {understood.focus.split("·").map((f) => f.trim()).filter(Boolean).slice(0, 6).map((f) => (
                  <span key={f} className="chip chip-mini">{f}</span>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <label style={{ ...fieldLabel, marginTop: "0.9rem" }}>Field / role</label>
      <input list="roles" placeholder="e.g. frontend engineer, nurse, product manager…"
        value={role} onChange={(e) => setRole(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter" && !busy && !offline) void primary(); }} />
      <datalist id="roles">{ROLE_SUGGESTIONS.map((r) => <option key={r} value={r} />)}</datalist>

      {/* Same fields, same order — tucked behind one line so the invitation reads as a
          paragraph, not a form dump (copy-table "Setup form intro"). */}
      <button type="button" className="link-quiet row" style={{ gap: "0.3rem", marginTop: "0.9rem", textDecoration: "none" }}
        onClick={() => setShowTuning((v) => !v)} aria-expanded={showTuning}>
        <ChevronDown size={12} style={{ transform: showTuning ? "none" : "rotate(-90deg)", transition: "transform 120ms ease" }} />
        Customize seniority, type & mode
      </button>
      {showTuning && (
        <div className="fade-up">
          <label style={{ ...fieldLabel, marginTop: "0.9rem" }}>Seniority</label>
          <div className="row" style={{ gap: "0.35rem", flexWrap: "wrap" }}>
            {SENIORITIES.map((s) => (
              <button key={s} type="button" className={`chip toggle${seniority === s ? " selected" : ""}`} onClick={() => setSeniority(s)}>{SENIORITY_LABELS[s]}</button>
            ))}
          </div>

          <label style={{ ...fieldLabel, marginTop: "0.9rem" }}>Interview type</label>
          <div className="row" style={{ gap: "0.35rem", flexWrap: "wrap" }}>
            {INTERVIEW_TYPES.map((t) => (
              <button key={t} type="button" className={`chip toggle${itype === t ? " selected" : ""}`} onClick={() => setItype(t)}>{TYPE_LABELS[t]}</button>
            ))}
          </div>

          <label style={{ ...fieldLabel, marginTop: "0.9rem" }}>Mode</label>
          <div className="row" style={{ gap: "0.35rem", flexWrap: "wrap" }}>
            {PRACTICE_MODES.map((m) => (
              <button key={m} type="button" className={`chip toggle${mode === m ? " selected" : ""}`} onClick={() => setMode(m)}>{MODE_LABELS[m]}</button>
            ))}
          </div>
          <div className="muted" style={{ fontSize: "var(--fs-xxs)", marginTop: 6, lineHeight: 1.5 }}>{MODE_HINTS[mode]}</div>
        </div>
      )}

      {/* Optional preview: structure the briefing WITHOUT starting yet, so the user can eyeball
          "here's what I understood" and tweak the fields first. Secondary + only when relevant. */}
      {needsReading && (
        <button className="btn btn-ghost btn-sm btn-pill" style={{ marginTop: "0.9rem" }}
          disabled={busy !== false || offline} onClick={() => void readBriefing()}>
          {busy === "reading" ? (<><Sparkles size={12} className="pulse-soft" /> Reading…</>) : (<><BookOpen size={13} /> Read my briefing first</>)}
        </button>
      )}

      {/* THE primary action — always visible, always says what it does. Auto-reads the briefing
          first when one is pasted, so this single button takes the user all the way to questions. */}
      <button className="btn btn-primary btn-pill" style={{ width: "100%", marginTop: "0.9rem" }}
        disabled={busy !== false || offline || (!role.trim() && !brief.trim())} onClick={() => void primary()}>
        {busy === "reading" ? (<><Sparkles size={14} className="pulse-soft" /> Understanding your briefing…</>)
          : busy === "generating" ? (<><Sparkles size={14} className="pulse-soft" /> Preparing your questions…</>)
          : (<><Sparkles size={15} /> Prepare my questions →</>)}
      </button>
      {/* READY-STATE R1 (2026-09-25) — the one thing this app will not do, said at the moment of
          commitment. The category leader's PAID tier is a live in-interview assistant (their own
          pricing page sells "Stealth Mode" as the thing the subscription unlocks). This app
          declined that lane in writing — Bogdan 2026-08-18, "no stealth shit, normal app" — and
          nothing on the first screen said so, which left our single clearest difference invisible
          while the doors below it cost money. States OUR shape only; their price is never named
          here, and never should be. */}
      <div className="muted practice-only">
        <ShieldCheck size={11} style={{ verticalAlign: "-1px", marginRight: 4 }} />
        Practice only — it never listens to a real interview, and there is no subscription.
      </div>
      <div className="muted" style={{ fontSize: "var(--fs-xxs)", marginTop: 6, lineHeight: 1.5, textAlign: "center" }}>
        {needsReading
          ? "Reading your briefing is one Oriora call; writing your questions is another."
          : "One Oriora call writes your five questions; each answer you have coached is one more."}
      </div>

      {busy && (
        <div aria-live="polite" style={{ marginTop: "0.85rem", display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 9 }}>
          <span className="muted row" style={{ fontSize: "var(--fs-xs)", gap: "0.4rem" }}>
            <Briefcase size={12} style={{ color: "var(--accent)" }} className="pulse-soft" />
            {busy === "reading" ? "Understanding your interview…" : "Writing your questions…"}
          </span>
          <div className="skeleton-line" style={{ width: "100%" }} />
          <div className="skeleton-line" style={{ width: "92%" }} />
          <div className="skeleton-line" style={{ width: "70%" }} />
        </div>
      )}

      {err && !busy && <BrainErrorNotice err={err} onRetry={() => void primary()} />}
    </div>
  );
}

function BrainErrorNotice({ err, onRetry }: { err: AskErr; onRetry: () => void }) {
  // Insufficient funds — point to the Oriora account on the web, no amounts shown.
  if (err.code === "insufficient_funds") {
    return (
      <div className="notice notice-info fade-up" style={{ marginTop: 12 }}>
        <Coins size={16} style={{ color: "var(--accent)", flexShrink: 0, marginTop: 1 }} />
        <div>
          <div style={{ fontSize: "var(--fs-sm)", fontWeight: 600 }}>Add funds to keep practicing</div>
          <div className="muted" style={{ fontSize: "var(--fs-xs)", marginTop: 2 }}>Your Oriora balance is too low. Add funds, then try again.</div>
        </div>
      </div>
    );
  }
  // BYOK gate — Managed AI blocked for this app; needs a vendor key.
  if (err.code === "managed_ai_blocked") {
    return (
      <div className="notice notice-info fade-up" style={{ marginTop: 12 }}>
        <Sparkles size={16} style={{ color: "var(--accent)", flexShrink: 0, marginTop: 1 }} />
        <div style={{ fontSize: "var(--fs-xs)" }}>{err.message}</div>
      </div>
    );
  }
  // Session expired — needs re-auth.
  if (err.code === "unauthenticated") {
    return (
      <div className="notice notice-info fade-up" style={{ marginTop: 12 }}>
        <Lock size={16} style={{ color: "var(--accent)", flexShrink: 0, marginTop: 1 }} />
        <div style={{ fontSize: "var(--fs-xs)" }}>{err.message}</div>
      </div>
    );
  }
  // Empty/validation — gentle, no retry button.
  if (err.code === "empty") {
    return <div className="muted fade-up" style={{ fontSize: "var(--fs-sm)", marginTop: 12 }}>{err.message}</div>;
  }
  // Network / unavailable — recoverable, offer a retry.
  return (
    <div className="notice notice-err fade-up" style={{ marginTop: 12, justifyContent: "space-between", alignItems: "center" }}>
      <span style={{ fontSize: "var(--fs-sm)" }}>{err.message}</span>
      <button className="btn btn-sm btn-pill" style={{ flexShrink: 0 }} onClick={onRetry}>
        <RefreshCw size={12} /> Retry
      </button>
    </div>
  );
}

// ── The practice room — one question at a time; AI call 2 per "Get feedback" ─────
// Two modes (Bogdan's review: "have different modes like real simulation mode vs early
// prep mode"): prep = untimed, hints open, coaching after every answer, refine loop.
// simulate = a countdown per question, no hints, answer → next → review it all at the end.
function PracticeModal({ sessionId, offline, onClose, onReviewNow }: {
  sessionId: string; offline: boolean; onClose: () => void; onReviewNow: (s: Session) => void;
}) {
  const [session, setSession] = useState<Session | null>(null);
  const [questions, setQuestions] = useState<SessionQuestion[] | null>(null);
  const [idx, setIdx] = useState(0);
  const [finished, setFinished] = useState(false);
  // LIVE MOCK INTERVIEW (parity-gate build 2026-08-18): the interviewer persona is
  // server-locked (realtime.ts); this just opens the fleet CallOverlay with THIS
  // session's context. Mock practice only — the persona refuses real-interview assist.
  const [liveCall, setLiveCall] = useState(false);

  const reload = useCallback(async () => {
    const s = await store.getSession(sessionId);
    const qs = await store.listQuestions(sessionId);
    setSession(s);
    setQuestions(qs);
    return qs;
  }, [sessionId]);

  useEffect(() => {
    void (async () => {
      const qs = await reload();
      // Resume at the first unanswered question.
      const first = qs.findIndex((q) => !q.answered_at);
      setIdx(first === -1 ? 0 : first);
      setFinished(first === -1 && qs.length > 0);
    })();
  }, [reload]);

  if (!session || questions === null) {
    return (
      <RoomShell meta="Practice" ariaLabel="Practice" onClose={onClose}>
        <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "0.5rem" }} aria-live="polite">
          {[0, 1, 2].map((i) => <div key={i} className="skeleton-block" style={{ height: 52 }} />)}
        </div>
      </RoomShell>
    );
  }

  const q = questions[Math.min(idx, questions.length - 1)];
  const answeredCount = questions.filter((x) => x.answered_at).length;

  function next() {
    const later = questions!.findIndex((x, i) => i > idx && !x.answered_at);
    if (later !== -1) { setIdx(later); return; }
    const earlier = questions!.findIndex((x) => !x.answered_at);
    if (earlier !== -1 && earlier !== idx) { setIdx(earlier); return; }
    setFinished(true);
  }

  if (finished) {
    const themes = session.themes;
    const uncoached = questions.filter((x) => x.answered_at && !parseFeedback(x.feedback_json)).length;
    const topTheme = themes.find((t) => THEME_NEXT_STEPS[t]);
    return (
      <RoomShell meta={`${session.role} · ${SENIORITY_LABELS[session.seniority]} · ${MODE_LABELS[session.mode]}`} ariaLabel="Session recap" onClose={onClose}>
        <h2 className="serif" style={{ fontSize: "1.4rem", margin: "0 0 0.25rem" }}>Session recap</h2>
        <div className="serif tnum" style={{ fontSize: "1.6rem", margin: "0.25rem 0" }}>
          {answeredCount} of {questions.length} answered
        </div>
        {themes.length > 0 && (
          <>
            <div className="muted" style={{ fontSize: "var(--fs-sm)", margin: "0.5rem 0 0.4rem" }}>What the coaching flagged this session:</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.3rem" }}>
              {themes.map((t) => <span key={t} className="chip chip-mini">{themeLabel(t)}</span>)}
            </div>
            {topTheme && (
              <div className="muted" style={{ fontSize: "var(--fs-xs)", marginTop: "0.6rem", lineHeight: 1.55 }}>
                <strong style={{ color: "var(--ink-2)" }}>Next step — {themeLabel(topTheme)}:</strong> {THEME_NEXT_STEPS[topTheme]}
              </div>
            )}
          </>
        )}
        {uncoached > 0 && (
          <div className="notice notice-info fade-up" style={{ marginTop: "0.85rem" }}>
            <MessageSquare size={15} style={{ color: "var(--accent)", flexShrink: 0, marginTop: 1 }} />
            <div style={{ fontSize: "var(--fs-xs)", lineHeight: 1.55 }}>
              {uncoached} answer{uncoached === 1 ? "" : "s"} waiting for coaching — review them now and get feedback
              on each (one Oriora call per answer).
            </div>
          </div>
        )}
        {themes.length === 0 && uncoached === 0 && (
          <div className="muted" style={{ fontSize: "var(--fs-sm)", marginTop: "0.5rem" }}>No answers recorded yet — open the session again anytime to continue.</div>
        )}
        <div className="muted" style={{ fontSize: "var(--fs-xs)", marginTop: "0.85rem", lineHeight: 1.55 }}>
          Open the session from the list anytime to reread everything — and keep your strongest
          answers with ★ so they build your own prep document.
        </div>
        <div className="row" style={{ justifyContent: "flex-end", gap: "0.5rem", marginTop: "1.25rem" }}>
          {uncoached > 0 ? (
            <>
              <button className="btn btn-pill" onClick={onClose}>Later</button>
              <button className="btn btn-primary btn-pill" onClick={() => onReviewNow(session)}>Review & get coaching</button>
            </>
          ) : (
            <>
              {answeredCount > 0 && <button className="btn btn-pill" onClick={() => onReviewNow(session)}>Review answers</button>}
              <button className="btn btn-primary btn-pill" onClick={onClose}>Done</button>
            </>
          )}
        </div>
      </RoomShell>
    );
  }

  const unanswered = questions.filter((x) => !x.answered_at);
  const liveCtx: InterviewCallContext = {
    role: session.role,
    seniority: session.seniority,
    interview_type: session.interview_type,
    ...(session.company ? { company: session.company } : {}),
    ...(session.focus ? { focus: session.focus } : {}),
    questions: unanswered.map((x) => x.question),
  };

  return (
    <RoomShell
      meta={[session.role, SENIORITY_LABELS[session.seniority], TYPE_LABELS[session.interview_type], MODE_LABELS[session.mode]].join(" · ")}
      progress={`Question ${idx + 1} of ${questions.length}`}
      simulate={session.mode === "simulate"}
      ariaLabel={`Question ${idx + 1} of ${questions.length}`}
      onClose={onClose}
    >
      {/* The live mock: the interviewer ASKS the remaining questions out loud, follows up,
          and gives a short spoken wrap-up. Practice only — never help with a real interview. */}
      {!offline && unanswered.length > 0 && (
        <div className="row" style={{ justifyContent: "flex-end", marginBottom: "0.6rem" }}>
          <button className="btn btn-sm btn-pill" onClick={() => setLiveCall(true)}
            title="A live voice mock — your interviewer asks the remaining questions and follows up. Practice only.">
            🎙 Live mock interview
          </button>
        </div>
      )}
      {liveCall && (
        <CallOverlay
          interview={liveCtx}
          onClose={() => { setLiveCall(false); void reload(); }}
          onTranscript={(callLines, secs) => {
            // The one-chat rule: the call's transcript lands in the SAME chat thread the
            // composer 📞 uses. Chat.tsx listens for this event (device-only hand-off).
            window.dispatchEvent(new CustomEvent("oriora-interview:call-transcript", {
              detail: { lines: callLines, seconds: secs },
            }));
          }}
        />
      )}
      <QuestionCard
        key={q.id}
        q={q}
        role={session.role}
        seniority={session.seniority}
        brief={session.brief}
        mode={session.mode}
        offline={offline}
        onAnswered={() => void reload()}
        onNext={next}
        isLastUnanswered={questions.filter((x) => !x.answered_at).length <= 1}
      />
    </RoomShell>
  );
}

function QuestionCard({ q, role, seniority, brief, mode, offline, onAnswered, onNext, isLastUnanswered }: {
  q: SessionQuestion; role: string; seniority: Seniority; brief: string; mode: PracticeMode; offline: boolean;
  onAnswered: () => void; onNext: () => void; isLastUnanswered: boolean;
}) {
  const stored = useMemo(() => parseFeedback(q.feedback_json), [q.feedback_json]);
  const [answer, setAnswer] = useState(q.answer);
  const [feedback, setFeedback] = useState<Feedback | null>(stored);
  const [showHint, setShowHint] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<AskErr | null>(null);
  const [revising, setRevising] = useState(false);
  const simulate = mode === "simulate";
  // Feedback exists AND we're not currently revising → show the coaching; otherwise show the editor.
  // (The "refine my answer" loop just flips `revising` back on, re-opening the editor pre-filled.)
  const answered = feedback !== null && !revising;

  // On-the-clock timer — real interviews are timed. Prep counts UP (see your pace);
  // simulate counts DOWN from a real answer budget ("time's up — wrap up" at zero, no hard
  // cutoff). The elapsed seconds are stored with the answer so the review shows your timing.
  // Display + storage are on-device; the clock is never part of the billed call.
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (answered || busy) return; // not editing, or a call is in flight → clock paused
    const t = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [answered, busy]);
  const remaining = SIMULATE_SECONDS - elapsed;

  async function coach() {
    const a = answer.trim();
    if (a.length < 10) { setErr({ code: "empty", message: "Write (or dictate) a sentence or two first." }); return; }
    setBusy(true); setErr(null);
    try {
      // The session's briefing rides along so the coaching weighs THIS job's requirements.
      const fb = await getFeedback(q.question, a, role, seniority, brief);
      await store.recordAnswer(q.id, a, fb, elapsed);
      setFeedback(fb);
      setRevising(false);
      onAnswered();
    } catch (e) {
      if (e instanceof BrainError) setErr({ code: e.code, message: e.message });
      else setErr({ code: "unavailable", message: "The request failed — please try again." });
    } finally { setBusy(false); }
  }

  // "Answer, go next, review it all at the end" — record the answer WITHOUT a billed call.
  // The recap then offers coaching on everything (the simulation flow; optional in prep too).
  async function saveAndNext() {
    const a = answer.trim();
    if (a.length < 10) { setErr({ code: "empty", message: "Write (or dictate) a sentence or two first." }); return; }
    setBusy(true); setErr(null);
    try {
      await store.recordAnswerOnly(q.id, a, elapsed);
      onAnswered();
      onNext();
    } catch {
      setErr({ code: "unavailable", message: "Couldn't save the answer — please try again." });
    } finally { setBusy(false); }
  }

  return (
    <div>
      {/* The question — with device-TTS read-aloud (fleet free-native-voice standard). */}
      <div className="row" style={{ gap: "0.6rem", alignItems: "flex-start", flexWrap: "nowrap" }}>
        <h2 className="room-question" style={{ flex: 1, minWidth: 0, ...(simulate ? { textAlign: "center" } : {}) }}>
          {q.question}
        </h2>
        <span style={{ flexShrink: 0, marginTop: 4 }} title="Hear the question read aloud, like an interviewer asking it"><SpeakButton text={q.question} /></span>
      </div>

      {/* Hints stay closed in simulation — a real interviewer doesn't hand you the rubric. */}
      {q.hint && !simulate && (
        <div style={{ marginTop: "0.6rem" }}>
          <button type="button" className="link row" style={{ gap: "0.25rem", fontSize: "var(--fs-xs)", background: "none", border: "none", padding: 0, cursor: "pointer" }}
            onClick={() => setShowHint((v) => !v)} aria-expanded={showHint}>
            <ChevronDown size={12} style={{ transform: showHint ? "none" : "rotate(-90deg)", transition: "transform 120ms ease" }} />
            What a good answer covers
          </button>
          {showHint && (
            <div className="notice notice-info fade-up" style={{ marginTop: 6 }}>
              <span style={{ fontSize: "var(--fs-xs)", lineHeight: 1.55 }}>{q.hint}</span>
            </div>
          )}
        </div>
      )}

      {!answered && (
        <>
          <div className="row" style={{ justifyContent: "space-between", alignItems: "center", marginTop: "1rem", gap: "0.5rem" }}>
            <label style={{ ...fieldLabel, margin: 0 }}>
              {revising ? "Refine your answer" : "Your answer"} <span className="muted" style={{ fontWeight: 400 }}>(type it, or dictate with the mic / iOS keyboard dictation)</span>
            </label>
            {simulate ? (
              <span className={`room-timer pressure${remaining <= 15 ? " low" : ""}`} style={{ flexShrink: 0 }}
                title={`Answer budget ${fmtClock(SIMULATE_SECONDS)} — like a real interview. The clock is on your device.`}>
                ⏱ {remaining > 0 ? fmtClock(remaining) : "time's up — wrap up"}
              </span>
            ) : (
              <span className="room-timer" title="Time on this question — real interviews are timed. The clock is on your device." style={{ flexShrink: 0 }}>
                ⏱ {fmtClock(elapsed)}
              </span>
            )}
          </div>
          <div className="row" style={{ gap: "0.4rem", alignItems: "flex-end", flexWrap: "nowrap" }}>
            <textarea rows={6} placeholder="Answer out loud first if you like — then get it down here…"
              value={answer} onChange={(e) => setAnswer(e.target.value)} style={{ flex: 1, minWidth: 0, resize: "vertical" }} />
            <span style={{ flexShrink: 0 }}><MicButton onText={(t) => setAnswer((v) => (v ? v + " " : "") + t)} disabled={busy} /></span>
          </div>

          <div className="row" style={{ justifyContent: "space-between", marginTop: "0.8rem", gap: "0.6rem", flexWrap: "wrap" }}>
            <span className="muted" style={{ fontSize: "var(--fs-xxs)", flex: 1, minWidth: 120 }}>
              {simulate
                ? "No coaching mid-simulation — answer, move on, review it all at the end."
                : "Coaching an answer is one Oriora call — about your answer, never a hiring verdict."}
            </span>
            <div className="row" style={{ gap: "0.4rem", flexShrink: 0, flexWrap: "wrap", justifyContent: "flex-end" }}>
              <button className="btn btn-sm btn-pill" disabled={busy} onClick={revising ? () => setRevising(false) : onNext}>{revising ? "Back" : (isLastUnanswered ? "Skip to recap" : "Skip")}</button>
              {simulate ? (
                <button className="btn btn-primary btn-sm btn-pill" disabled={busy || answer.trim().length < 10} onClick={saveAndNext}>
                  {isLastUnanswered ? "Save & finish" : "Save & next"}
                </button>
              ) : (
                <>
                  <button className="btn btn-sm btn-pill" disabled={busy || answer.trim().length < 10} onClick={saveAndNext}
                    title="Record this answer and move on — get coaching on everything at the end">
                    {isLastUnanswered ? "Save, coach later" : "Save & next"}
                  </button>
                  <button className="btn btn-primary btn-sm btn-pill" disabled={busy || offline || answer.trim().length < 10} onClick={coach}>
                    {busy ? (<><Sparkles size={12} className="pulse-soft" /> Coaching…</>) : (<><Sparkles size={13} /> {revising ? "Get fresh feedback" : "Get feedback"}</>)}
                  </button>
                </>
              )}
            </div>
          </div>

          {busy && !simulate && (
            <div aria-live="polite" style={{ marginTop: "0.85rem", display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 9 }}>
              <span className="muted row" style={{ fontSize: "var(--fs-xs)", gap: "0.4rem" }}>
                <MessageSquare size={12} style={{ color: "var(--accent)" }} className="pulse-soft" /> Reading your answer…
              </span>
              <div className="skeleton-line" style={{ width: "100%" }} />
              <div className="skeleton-line" style={{ width: "88%" }} />
              <div className="skeleton-line" style={{ width: "64%" }} />
            </div>
          )}
          {/* In simulation the only failable action is the local save — retry that, never a billed call. */}
          {err && !busy && <BrainErrorNotice err={err} onRetry={simulate ? saveAndNext : coach} />}
        </>
      )}

      {answered && feedback && (
        <div className="fade-up" style={{ marginTop: "0.9rem" }} aria-live="polite">
          <div className="stat-lbl" style={{ marginBottom: "0.2rem" }}>Your answer{elapsed > 0 ? ` · answered in ${fmtClock(elapsed)}` : ""}</div>
          <p className="muted" style={{ fontSize: "var(--fs-sm)", lineHeight: 1.6, margin: "0 0 0.75rem", whiteSpace: "pre-wrap" }}>{answer.trim() || q.answer}</p>
          <FeedbackView fb={feedback} />
          <div className="row" style={{ marginTop: 12, justifyContent: "space-between", gap: "0.6rem", flexWrap: "wrap" }}>
            <div className="row" style={{ gap: "0.4rem" }}>
              <CopyButton text={copyableFeedback(q.question, feedback)} />
              <SaveBestButton question={q.question} answer={answer.trim() || q.answer} role={role} />
            </div>
            <div className="row" style={{ gap: "0.4rem", flexShrink: 0 }}>
              <button className="btn btn-sm btn-pill" onClick={() => { setRevising(true); setErr(null); }}
                title="Revise your answer and get fresh coaching on the improved version">
                <RefreshCw size={12} /> Refine my answer
              </button>
              <button className="btn btn-primary btn-sm btn-pill" onClick={onNext}>{isLastUnanswered ? "Finish" : "Next question"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ★ Keep this answer — saves into the on-device best-answers library (re-saving the same
// question updates it; the library is the "document to pull from" before the real interview).
function SaveBestButton({ question, answer, role }: { question: string; answer: string; role: string }) {
  const [done, setDone] = useState(false);
  return (
    <button className="copy-btn" title={done ? "Saved to Best answers" : "Save as best answer — keep it in your library"}
      aria-label={done ? "Saved to Best answers" : "Save as best answer"}
      onClick={async () => {
        try { await store.saveBestAnswer(question, answer, role); setDone(true); setTimeout(() => setDone(false), 2000); }
        catch { /* nothing to surface inline; the library view is the source of truth */ }
      }}>
      <span aria-hidden>{done ? <Check size={13} /> : <Star size={13} />}</span>
      <span className="copy-btn-lbl">{done ? "Saved ★" : "Save best"}</span>
    </button>
  );
}

// The structured coaching card — strengths / gaps / STAR note / one better-phrased example.
function FeedbackView({ fb }: { fb: Feedback }) {
  return (
    <div className="coach">
      {fb.strengths.length > 0 && (
        <div className="coach-block">
          <div className="stat-lbl" style={{ marginBottom: "0.1rem" }}>What worked</div>
          {fb.strengths.map((s, i) => (
            <div key={i} className="fact"><span className="fact-dot good" /><span className="fact-text">{s}</span></div>
          ))}
        </div>
      )}
      {fb.gaps.length > 0 && (
        <div className="coach-block">
          <div className="stat-lbl" style={{ marginBottom: "0.1rem" }}>What to tighten</div>
          {fb.gaps.map((s, i) => (
            <div key={i} className="fact"><span className="fact-dot watch" /><span className="fact-text">{s}</span></div>
          ))}
        </div>
      )}
      {fb.star_note && (
        <div className="coach-block">
          <div className="stat-lbl" style={{ marginBottom: "0.1rem" }}>Structure (STAR)</div>
          <div className="fact"><span className="fact-dot" /><span className="fact-text">{fb.star_note}</span></div>
        </div>
      )}
      {fb.better_example && (
        <div className="coach-example">
          <div className="coach-example-lbl">One part of your answer, phrased stronger</div>
          <div className="coach-example-text">"{fb.better_example}"</div>
          <span style={{ display: "inline-block", marginTop: 4 }}><SpeakButton text={fb.better_example} /></span>
        </div>
      )}
      {fb.themes.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.3rem", marginTop: "0.6rem" }}>
          {fb.themes.map((t) => <span key={t} className="chip chip-mini" title="Tracked in your progress over time">{themeLabel(t)}</span>)}
        </div>
      )}
      <div style={{ marginTop: 8 }}><OrioraBadge /></div>
      <div className="muted" style={{ fontSize: "var(--fs-xxs)", marginTop: 8 }}>
        Coaching on your own answer — not a hiring prediction, and the example only re-phrases what you actually said.
      </div>
    </div>
  );
}

// Ghost copy-button for AI results (DESIGN-SYSTEM §7.5 — every Oriora result is copyable).
function CopyButton({ text, label = "Copy feedback" }: { text: string; label?: string }) {
  const [done, setDone] = useState(false);
  const copy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(text);
      setDone(true);
      setTimeout(() => setDone(false), 1500);
    } catch { /* clipboard blocked — silently no-op */ }
  }, [text]);
  return (
    <button className="copy-btn" onClick={copy} aria-label={done ? "Copied" : label} title={done ? "Copied" : label}>
      <span aria-hidden>{done ? <Check size={13} /> : <Copy size={13} />}</span>
      <span className="copy-btn-lbl">{done ? "Copied" : label}</span>
      <span aria-live="polite" className="sr-only">{done ? "Copied to clipboard" : ""}</span>
    </button>
  );
}

function copyableFeedback(question: string, fb: Feedback): string {
  const parts = [
    `Q: ${question}`,
    fb.strengths.length ? `What worked:\n${fb.strengths.map((s) => `• ${s}`).join("\n")}` : "",
    fb.gaps.length ? `What to tighten:\n${fb.gaps.map((s) => `• ${s}`).join("\n")}` : "",
    fb.star_note ? `Structure: ${fb.star_note}` : "",
    fb.better_example ? `Stronger phrasing: "${fb.better_example}"` : "",
  ];
  return parts.filter(Boolean).join("\n\n");
}

function SessionTile({ s, onOpen, onReview, onDelete }: {
  s: Session; onOpen: () => void; onReview: () => void; onDelete: () => void;
}) {
  const complete = s.question_count > 0 && s.answered_count >= s.question_count;
  // R5 — shelf grammar: full role name (wraps, never truncates), no icon tile, hairline progress.
  return (
    <div className="shelf-row">
      <button className="shelf-main" onClick={onOpen} aria-label={`Open ${s.role} practice`}>
        <div className="row" style={{ gap: "0.5rem" }}>
          <span className="shelf-role" style={{ textTransform: "capitalize" }}>{s.role}</span>
          {complete
            ? <span className="badge badge-make" style={{ flexShrink: 0 }}>complete</span>
            : <span className="badge badge-need" style={{ flexShrink: 0 }}>in progress</span>}
        </div>
        <div className="shelf-meta">{SENIORITY_LABELS[s.seniority]} · {TYPE_LABELS[s.interview_type]} · {MODE_LABELS[s.mode]}{s.company ? ` · ${s.company}` : ""}</div>
        {s.question_count > 0 && (
          <div className="shelf-progress" aria-hidden>
            <span style={{ width: `${Math.min(100, Math.round((s.answered_count / s.question_count) * 100))}%`, background: complete ? "var(--success)" : "var(--accent)" }} />
          </div>
        )}
        <div className="shelf-meta tnum">{s.answered_count}/{s.question_count} answered · {relativeDays(s.created_at)}</div>
      </button>
      <div className="shelf-actions">
        <IconBtn title="Review answers & coaching" onClick={onReview}><History size={15} /></IconBtn>
        <IconBtn title="Delete session" danger onClick={onDelete}><Trash2 size={15} /></IconBtn>
      </div>
    </div>
  );
}

function IconBtn({ title, onClick, children, on, danger }: { title: string; onClick: () => void; children: React.ReactNode; on?: boolean; danger?: boolean }) {
  return (
    <button className={`icon-btn${on ? " on" : ""}${danger ? " danger" : ""}`} title={title} aria-label={title}
      onClick={(ev) => { ev.stopPropagation(); onClick(); }}>
      {children}
    </button>
  );
}

// Review of a past session: every question, the answer given, the coaching — and for
// answers saved without coaching ("answer, go next, review at the end"), coaching HERE:
// one Oriora call per answer, or "Coach all" for the whole set (count disclosed).
function ReviewModal({ session, offline, onChanged, onClose }: {
  session: Session; offline: boolean; onChanged: () => void; onClose: () => void;
}) {
  const [questions, setQuestions] = useState<SessionQuestion[] | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [coachingAll, setCoachingAll] = useState(false);
  const [errMsg, setErrMsg] = useState<string | null>(null);

  const reload = useCallback(async () => setQuestions(await store.listQuestions(session.id)), [session.id]);
  useEffect(() => { void reload(); }, [reload]);

  const uncoached = (questions ?? []).filter((q) => q.answered_at && q.answer.trim() && !parseFeedback(q.feedback_json));
  const answered = (questions ?? []).filter((q) => q.answered_at && q.answer.trim());

  // ── The session report (parity-gate mock loop): ONE billed call over the whole transcript
  // digest → summary, cross-answer strengths/gaps, next steps, and the weakest questions.
  // Stored on the session; the weakest questions drive the ZERO-call re-practice loop below.
  const [report, setReport] = useState<SessionReport | null>(() => parseReport(session.report_json));
  const [reportBusy, setReportBusy] = useState(false);
  const [repracticeBusy, setRepracticeBusy] = useState(false);

  async function writeReport() {
    setReportBusy(true); setErrMsg(null);
    try {
      const items = answered.map((q) => {
        const fb = parseFeedback(q.feedback_json);
        return {
          n: q.idx + 1,
          question: q.question,
          answer: q.answer,
          ...(fb && fb.gaps.length ? { gaps: fb.gaps.join(" · ") } : {}),
          ...(fb && fb.themes.length ? { themes: fb.themes.join(",") } : {}),
        };
      });
      const r = await getSessionReport(session, items);
      await store.recordReport(session.id, r);
      setReport(r);
      onChanged();
    } catch (e) {
      setErrMsg(e instanceof BrainError ? e.message : "The report failed — please try again.");
    } finally { setReportBusy(false); }
  }

  // Re-practice the weakest questions — a NEW session from the report's picks (question +
  // hint copied; same setup and briefing). Local only: no AI call until the user answers.
  async function repractice() {
    if (!report || questions === null) return;
    const picks = report.repractice
      .map((n) => questions.find((q) => q.idx === n - 1))
      .filter((q): q is SessionQuestion => Boolean(q));
    if (picks.length === 0) return;
    setRepracticeBusy(true); setErrMsg(null);
    try {
      await store.createSession({
        role: session.role, seniority: session.seniority, interview_type: session.interview_type,
        mode: "prep", company: session.company, focus: session.focus, brief: session.brief,
        questions: picks.map((q) => ({ question: q.question, hint: q.hint })),
      });
      onChanged();
      onClose(); // the new session takes the chair on Home
    } catch {
      setErrMsg("Couldn't start the re-practice session — please try again.");
    } finally { setRepracticeBusy(false); }
  }

  async function coachOne(q: SessionQuestion): Promise<boolean> {
    setBusyId(q.id); setErrMsg(null);
    try {
      // The session's briefing rides along so the coaching weighs THIS job's requirements.
      const fb = await getFeedback(q.question, q.answer, session.role, session.seniority, session.brief);
      await store.recordAnswer(q.id, q.answer, fb, q.answer_seconds);
      await reload();
      onChanged();
      return true;
    } catch (e) {
      setErrMsg(e instanceof BrainError ? e.message : "Coaching failed — please try again.");
      return false;
    } finally { setBusyId(null); }
  }

  async function coachAll() {
    setCoachingAll(true);
    try {
      for (const q of uncoached) {
        if (!(await coachOne(q))) break; // an error stops the run — nothing silently skipped
      }
    } finally { setCoachingAll(false); }
  }

  // R3 — the review reads like an interview TRANSCRIPT: full-screen, numbered Q/A entries,
  // the coaching set against each answer, recurring themes in the header.
  const themeCounts = new Map<string, number>();
  for (const q of questions ?? []) {
    for (const t of parseFeedback(q.feedback_json)?.themes ?? []) themeCounts.set(t, (themeCounts.get(t) ?? 0) + 1);
  }
  return (
    <RoomShell meta={session.role} ariaLabel="Interview transcript" onClose={onClose}>
      <div className="transcript-head">
        <h2 className="transcript-title">Interview transcript</h2>
        <div className="transcript-meta">
          {[SENIORITY_LABELS[session.seniority], TYPE_LABELS[session.interview_type], MODE_LABELS[session.mode], relativeDays(session.created_at)].join(" · ")}
          {session.company ? ` · ${session.company}` : ""}
        </div>
        {themeCounts.size > 0 && (
          <div className="transcript-themes">
            {[...themeCounts.entries()].sort((a, b) => b[1] - a[1]).map(([t, n]) => (
              <span key={t} className="chip chip-mini tnum" title="Flagged across this session's coaching">{themeLabel(t)} ×{n}</span>
            ))}
          </div>
        )}
      </div>
      {uncoached.length > 0 && (
        <div className="notice notice-info" style={{ marginBottom: "0.75rem", justifyContent: "space-between", alignItems: "center", gap: "0.6rem" }}>
          <span style={{ fontSize: "var(--fs-xs)", lineHeight: 1.5 }}>
            {uncoached.length} answer{uncoached.length === 1 ? "" : "s"} not coached yet.
          </span>
          <button className="btn btn-primary btn-sm btn-pill" style={{ flexShrink: 0 }} disabled={offline || coachingAll || busyId !== null} onClick={() => void coachAll()}
            title="One Oriora call per answer">
            {coachingAll ? (<><Sparkles size={12} className="pulse-soft" /> Coaching…</>) : (<><Sparkles size={13} /> Coach all — {uncoached.length} {uncoached.length === 1 ? "call" : "calls"}</>)}
          </button>
        </div>
      )}
      {errMsg && <div className="notice notice-err" style={{ marginBottom: "0.6rem" }}><span style={{ fontSize: "var(--fs-sm)" }}>{errMsg}</span></div>}
      {questions === null ? (
        <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "0.5rem" }} aria-live="polite">
          {[0, 1, 2].map((i) => <div key={i} className="skeleton-block" style={{ height: 52 }} />)}
        </div>
      ) : (
        <div>
          {questions.map((q) => {
            const fb = parseFeedback(q.feedback_json);
            return (
              <div key={q.id} className="transcript-entry">
                <div className="transcript-q">
                  <span className="transcript-q-idx serif tnum">{String(q.idx + 1).padStart(2, "0")}</span>
                  <span className="transcript-q-text">{q.question}</span>
                </div>
                {q.answered_at ? (
                  <>
                    <div className="stat-lbl" style={{ margin: "0.15rem 0 0.15rem", paddingLeft: "1.5rem" }}>
                      Your answer{q.answer_seconds > 0 ? ` · answered in ${fmtClock(q.answer_seconds)}` : ""}
                    </div>
                    <div className="transcript-a">{q.answer}</div>
                    {fb ? (
                      <div style={{ paddingLeft: "1.5rem" }}>
                        <FeedbackView fb={fb} />
                        <div style={{ marginTop: 8 }}>
                          <SaveBestButton question={q.question} answer={q.answer} role={session.role} />
                        </div>
                      </div>
                    ) : q.answer.trim() ? (
                      <div style={{ paddingLeft: "1.5rem" }}>
                        <button className="btn btn-sm btn-pill" disabled={offline || busyId !== null || coachingAll} onClick={() => void coachOne(q)}
                          title="One Oriora call — structured coaching on this answer">
                          {busyId === q.id ? (<><Sparkles size={12} className="pulse-soft" /> Coaching…</>) : (<><Sparkles size={13} /> Get coaching</>)}
                        </button>
                      </div>
                    ) : null}
                  </>
                ) : (
                  <div className="transcript-skip">Skipped</div>
                )}
              </div>
            );
          })}
          <div className="muted" style={{ fontSize: "var(--fs-xs)", lineHeight: 1.6, marginTop: "0.5rem" }}>
            Keep your strongest lines with the star mark — they build your own best-answers library.
          </div>

          {/* ── Session debrief — the whole-mock report + the re-practice loop ── */}
          {answered.length > 0 && (
            <div style={{ marginTop: "1.1rem" }}>
              <div className="sec-head"><h2><MessageSquare size={16} style={{ color: "var(--accent)" }} /> Session debrief</h2></div>
              {report ? (
                <div className="coach fade-up">
                  {report.summary && <p style={{ fontSize: "var(--fs-sm)", lineHeight: 1.6, margin: "0 0 0.6rem" }}>{report.summary}</p>}
                  {report.strengths.length > 0 && (
                    <div className="coach-block">
                      <div className="stat-lbl" style={{ marginBottom: "0.1rem" }}>What kept working</div>
                      {report.strengths.map((s, i) => (
                        <div key={i} className="fact"><span className="fact-dot good" /><span className="fact-text">{s}</span></div>
                      ))}
                    </div>
                  )}
                  {report.gaps.length > 0 && (
                    <div className="coach-block">
                      <div className="stat-lbl" style={{ marginBottom: "0.1rem" }}>What kept going missing</div>
                      {report.gaps.map((s, i) => (
                        <div key={i} className="fact"><span className="fact-dot watch" /><span className="fact-text">{s}</span></div>
                      ))}
                    </div>
                  )}
                  {report.next_steps.length > 0 && (
                    <div className="coach-block">
                      <div className="stat-lbl" style={{ marginBottom: "0.1rem" }}>Next session, practice</div>
                      {report.next_steps.map((s, i) => (
                        <div key={i} className="fact"><span className="fact-dot" /><span className="fact-text">{s}</span></div>
                      ))}
                    </div>
                  )}
                  <div style={{ marginTop: 8 }}><OrioraBadge /></div>
                  <div className="muted" style={{ fontSize: "var(--fs-xxs)", marginTop: 8 }}>
                    A debrief of your answers — not a hiring prediction or a verdict on you.
                  </div>
                  <div className="row" style={{ marginTop: 12, justifyContent: "space-between", gap: "0.6rem", flexWrap: "wrap" }}>
                    <div className="row" style={{ gap: "0.4rem" }}>
                      <CopyButton text={copyableReport(session.role, report)} label="Copy debrief" />
                      <button className="copy-btn" disabled={offline || reportBusy} onClick={() => void writeReport()}
                        title="Write a fresh debrief over the answers as they are now — one Oriora call" aria-label="Rewrite debrief — 1 call">
                        <span aria-hidden>{reportBusy ? <Sparkles size={13} className="pulse-soft" /> : <RefreshCw size={13} />}</span>
                        <span className="copy-btn-lbl">{reportBusy ? "Writing…" : "Rewrite — 1 call"}</span>
                      </button>
                    </div>
                    {report.repractice.length > 0 && (
                      <button className="btn btn-primary btn-sm btn-pill" disabled={repracticeBusy} onClick={() => void repractice()}
                        title="Starts a new practice with these questions — no Oriora call until you answer">
                        {repracticeBusy
                          ? (<><Sparkles size={12} className="pulse-soft" /> Setting up…</>)
                          : (<><RefreshCw size={13} /> Practice Q{report.repractice.join(", Q")} again — free</>)}
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <div className="row" style={{ justifyContent: "space-between", alignItems: "center", gap: "0.6rem", flexWrap: "wrap" }}>
                  <span className="muted" style={{ fontSize: "var(--fs-xs)", lineHeight: 1.5, flex: 1, minWidth: 160 }}>
                    One report across the whole session — what kept working, what kept going missing,
                    and which answers are worth another go.{uncoached.length > 0 ? " Coaching the remaining answers first makes it sharper." : ""}
                  </span>
                  <button className="btn btn-primary btn-sm btn-pill" style={{ flexShrink: 0 }}
                    disabled={offline || reportBusy || coachingAll || busyId !== null} onClick={() => void writeReport()}
                    title="One Oriora call — a debrief grounded in your answers">
                    {reportBusy ? (<><Sparkles size={12} className="pulse-soft" /> Writing…</>) : (<><Sparkles size={13} /> Write my session report — 1 call</>)}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
      <div className="row" style={{ justifyContent: "flex-end", marginTop: "1.25rem" }}>
        <button className="btn btn-pill" onClick={onClose}>Close</button>
      </div>
    </RoomShell>
  );
}

function copyableReport(role: string, r: SessionReport): string {
  const parts = [
    `Session debrief — ${role}`,
    r.summary,
    r.strengths.length ? `What kept working:\n${r.strengths.map((s) => `• ${s}`).join("\n")}` : "",
    r.gaps.length ? `What kept going missing:\n${r.gaps.map((s) => `• ${s}`).join("\n")}` : "",
    r.next_steps.length ? `Next session, practice:\n${r.next_steps.map((s) => `• ${s}`).join("\n")}` : "",
  ];
  return parts.filter(Boolean).join("\n\n");
}

// ── Best answers — the kept library (visible from the FIRST save; ★ on any coached answer
// adds here; the chat curates it too: "show my best answers", "save that", "delete …"). ──
function BestAnswersSection({ best, onChanged }: { best: BestAnswer[]; onChanged: () => void }) {
  const [openId, setOpenId] = useState<string | null>(null);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const toDelete = best.find((b) => b.id === confirmId) ?? null;
  return (
    <>
      <div className="sec-head">
        <h2><img src={markBestAnswer} width={16} height={16} alt="" /> Best answers <span className="muted tnum" style={{ fontWeight: 400 }}>({best.length})</span></h2>
        <span className="muted row" style={{ fontSize: "var(--fs-xs)", gap: "0.25rem" }}><Lock size={11} /> on device</span>
      </div>
      {/* R6 — keepsakes: the user's own lines, kept with care; no tile chrome. */}
      <div style={{ marginBottom: "1.1rem" }}>
        {best.map((b) => {
          const open = openId === b.id;
          return (
            <div key={b.id} className="keepsake">
              <button style={{ width: "100%", minWidth: 0, cursor: "pointer", background: "none", border: "none", textAlign: "left", padding: 0 }}
                onClick={() => setOpenId(open ? null : b.id)} aria-expanded={open}>
                <div className="keepsake-q" style={open ? {} : { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block" }}>
                  {b.question}
                </div>
                <div className="keepsake-a" style={open ? {} : { display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                  {b.answer}
                </div>
                {open && (b.note || b.role) && (
                  <div className="keepsake-note" style={{ textTransform: "capitalize" }}>
                    {b.note ? `Good for: ${b.note}` : ""}{b.note && b.role ? " · " : ""}{b.role}{(b.note || b.role) ? " · " : ""}kept {relativeDays(b.updated_at)}
                  </div>
                )}
              </button>
              <div className="keepsake-actions">
                <CopyButton text={`Q: ${b.question}\n\nA: ${b.answer}`} label="Copy answer" />
                <IconBtn title="Remove from best answers" danger onClick={() => setConfirmId(b.id)}><Trash2 size={15} /></IconBtn>
              </div>
            </div>
          );
        })}
      </div>
      {toDelete && (
        <ModalShell title="Remove best answer?" onCancel={() => setConfirmId(null)}>
          <p className="muted" style={{ fontSize: "var(--fs-sm)", margin: "0 0 1.25rem", lineHeight: 1.55 }}>
            Remove the kept answer for <strong style={{ color: "var(--ink-1)" }}>{toDelete.question}</strong>? This can't be undone.
          </p>
          <div className="row" style={{ justifyContent: "flex-end", gap: "0.5rem" }}>
            <button className="btn btn-pill" onClick={() => setConfirmId(null)}>Cancel</button>
            <button className="btn btn-danger btn-pill" onClick={async () => { await store.deleteBestAnswer(toDelete.id); setConfirmId(null); onChanged(); }}>Remove</button>
          </div>
        </ModalShell>
      )}
    </>
  );
}

const fieldLabel: React.CSSProperties = { display: "block", fontWeight: 600, fontSize: "var(--fs-sm)", marginBottom: "0.45rem" };

// R2/R3 — the practice room and the transcript are full-screen ROUTES, not modals. This shell
// owns the viewport; ModalShell below stays for small confirms only (delete etc.).
function RoomShell({ meta, progress, simulate, ariaLabel, onClose, children }: {
  meta: string; progress?: string; simulate?: boolean; ariaLabel: string; onClose: () => void; children: React.ReactNode;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div role="dialog" aria-modal="true" aria-label={ariaLabel} className={`room${simulate ? " room--simulate" : ""}`}>
      <div className="room-inner">
        <div className="room-top">
          <div style={{ minWidth: 0 }}>
            <div className="room-meta" style={{ textTransform: "uppercase", letterSpacing: "0.04em" }}>{meta}</div>
            {progress && <div className="room-progress tnum">{progress}</div>}
          </div>
          <button className="icon-btn" aria-label="Close" onClick={onClose} style={{ flexShrink: 0 }}><X size={16} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function ModalShell({ title, subtitle, children, onCancel, wide }: { title: string; subtitle?: string; children: React.ReactNode; onCancel: () => void; wide?: boolean }) {
  // Esc closes; restore focus is browser-default. Scrim click closes.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onCancel(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onCancel]);
  return (
    <div role="dialog" aria-modal="true" aria-label={title} className="sheet-scrim" onClick={onCancel}>
      <div className="sheet sheet-center" onClick={(e) => e.stopPropagation()} style={{ width: wide ? "min(480px, calc(100% - 2rem))" : "min(400px, calc(100% - 2rem))", paddingBottom: "calc(1.5rem + env(safe-area-inset-bottom))" }}>
        <div className="row" style={{ justifyContent: "space-between", alignItems: "flex-start", marginBottom: "1.1rem", gap: "0.6rem" }}>
          <div style={{ minWidth: 0 }}>
            <div className="serif" style={{ fontWeight: 600, fontSize: "1.2rem" }}>{title}</div>
            {subtitle && <div className="muted" style={{ fontSize: "var(--fs-xs)", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{subtitle}</div>}
          </div>
          <button className="icon-btn" aria-label="Close" onClick={onCancel} style={{ flexShrink: 0, marginTop: -2 }}><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
